// client\src\contexts\CallContext.jsx

import { forceLogoutAndRedirect, fetchWithAuth } from '../services/apiClient';
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuth } from '../hooks/useAuth';
import { callHistoryService } from '../services/callHistoryService';
import { messageHandlers, pickCallerName } from '../services/messageHandlers';
import { configService } from '../services/configService';
import { useReliableCalls } from '../hooks/useReliableCalls';
import { useProactiveRefresh } from '../hooks/useProactiveRefresh';

const CallContext = createContext(null);

export const useCallContext = () => {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('useCallContext must be used within CallProvider');
  return ctx;
};

export function CallProvider({ children }) {
  const { isAuthenticated, currentUser, isReceptionist, isAuthLoading, handleLogin, handleLogout: authLogout } = useAuth();

  useProactiveRefresh(isAuthenticated);
  
  const BASE_URL = import.meta.env.VITE_BASE_URL;

  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const [currentCalls, setCurrentCalls] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [callInfo, setCallInfo] = useState(null);
  const [loadingStage, setLoadingStage] = useState(0);
  const [dashboardAnimationState, setDashboardAnimationState] = useState('closed');

  const [isCallActive, setIsCallActive] = useState(false);
  const [callStartTime, setCallStartTime] = useState(null);
  const [currentCallId, setCurrentCallId] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [callDuration, setCallDuration] = useState(0);

  const [isSearching, setIsSearching] = useState(false);
  const [searchPhoneNumber, setSearchPhoneNumber] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [isSMSOpen, setIsSMSOpen] = useState(false);
  const [smsContact, setSmsContact] = useState(null);
  // Pre-filled composer body (e.g. the agent's PBX chat/video link). The
  // composer stops there — the agent still presses Send.
  const [smsDraft, setSmsDraft] = useState('');

  const [appConfig, setAppConfig] = useState({
    productionMode: true,
    features: { searchEnabled: false },
  });

  const handleWebSocketAuthFailure = useCallback(() => {
    console.error('🔒 WebSocket reported auth failure — forcing logout');
    forceLogoutAndRedirect();
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      const config = await configService.getAppConfig();
      console.log('📋 App config loaded:', config);
      setAppConfig(config);
    };
    fetchConfig();
  }, []);

  const audioRef = useRef(null);
  const visibilityCheckIntervalRef = useRef(null);
  const lastVisibilityCheckRef = useRef(Date.now());
  const currentCallIdRef = useRef(null);
  const showCustomerPopupRef = useRef(false);
  const callStatusRef = useRef(null);

  useEffect(() => { currentCallIdRef.current = currentCallId; }, [currentCallId]);
  useEffect(() => { showCustomerPopupRef.current = showCustomerPopup; }, [showCustomerPopup]);
  useEffect(() => { callStatusRef.current = callStatus; }, [callStatus]);

  const [lastReliableMessage, setLastReliableMessage] = useState(null);
  const { reliableCalls, clearAll: clearReliableCalls } = useReliableCalls(lastReliableMessage, currentUser, connectionStatus);

  // ✅ Derive activeCall from reliableCalls
  const activeCall = reliableCalls.find(c => c.status === 'Connected') || null;

  useEffect(() => {
    if (isAuthenticated) {
      import('../pages/Dashboard');
    }
  }, [isAuthenticated]);

  const handleWebSocketMessage = useCallback((message) => {
    setLastReliableMessage(message);

    if (message.type === 'vinsolutions_not_found') {
      console.log('ℹ️ VinSolutions data not found (404) for call:', message.callInfo?.callId);
      const callId = message.callInfo?.callId;
      if (!callId) return;

      setCurrentCalls(prev =>
        prev.map(call =>
          call.callId === callId
            ? { ...call, vinSolutionsLoaded: true, vinSolutions404: true, customerData: null, loadingStage: 4, lastUpdate: new Date().toISOString() }
            : call
        )
      );

      if (currentCallIdRef.current === callId && showCustomerPopupRef.current) {
        console.log('ℹ️ No VinSolutions data available for this customer');
        setLoadingStage(4);
      }
      return;
    }

    if (message.type === 'tekion_not_found') {
      console.log('ℹ️ Tekion data not found (404) for call:', message.callInfo?.callId);
      const callId = message.callInfo?.callId;
      if (!callId) return;

      setCurrentCalls(prev =>
        prev.map(call => {
          if (call.callId !== callId) return call;
          const isFullyLoaded = call.vinSolutionsLoaded || call.vinSolutions404;
          console.log(`📊 Tekion 404 - VinSolutions loaded: ${call.vinSolutionsLoaded}, Setting fullyLoaded: ${isFullyLoaded}`);
          return {
            ...call, tekionLoaded: true, tekion404: true,
            fullyLoaded: isFullyLoaded,
            loadingStage: isFullyLoaded ? 5 : call.loadingStage,
            lastUpdate: new Date().toISOString(),
          };
        })
      );

      if (currentCallIdRef.current === callId && showCustomerPopupRef.current) {
        console.log('ℹ️ No Tekion data available for this customer');
        setLoadingStage(5);
      }
      return;
    }

    if (message.type === 'tekion_retry') {
      console.log('🔄 Tekion service unavailable (503), retrying...', message.callInfo?.callId);
      return;
    }

    if (message.type === 'twilio_data') {
      console.log('📞 TWILIO DATA RECEIVED:', message);
      const callId = message.callInfo?.callId;
      if (!callId) return;

      setCurrentCalls(prev =>
        prev.map(call => {
          if (call.callId !== callId) return call;
          const updatedCustomerData = { ...(call.customerData || {}), twilioData: message.data, hasTwilioData: true };
          // Priority: Tekion → CRM (Vinsolutions) → Twilio
          return {
            ...call,
            callerName: pickCallerName(updatedCustomerData, call.callerName),
            customerData: updatedCustomerData,
            lastUpdate: new Date().toISOString(),
          };
        })
      );

      if (currentCallIdRef.current === callId && showCustomerPopupRef.current) {
        const mergedCustomerData = { ...(customerData || {}), twilioData: message.data, hasTwilioData: true };
        setCustomerData(mergedCustomerData);
        // Priority: Tekion → CRM (Vinsolutions) → Twilio
        setCallInfo(prev => ({ ...prev, callerName: pickCallerName(mergedCustomerData, prev?.callerName) }));
      }
      return;
    }

    if (message.type === 'contact_detail_update') {
      // Lazy on-demand fetch result for a peer VinSolutions contact.
      // The matching shell entry in allContactsData[] gets its leads /
      // vehicles / sales-rep fields filled in and detailsLoaded flipped
      // to true so Dashboard's useEffect stops re-firing for this index.
      const { callId, contactId, detail, error } = message;
      if (!callId || !contactId) return;

      const mergeIntoContacts = (allContactsData) => {
        if (!Array.isArray(allContactsData)) return allContactsData;
        const idx = allContactsData.findIndex(
          (c) => c?.contact?.contactId?.toString() === contactId.toString()
        );
        if (idx === -1) return allContactsData;
        const target = allContactsData[idx];
        if (error) {
          return allContactsData.map((c, i) =>
            i === idx ? { ...c, detailsLoaded: true, detailError: error } : c
          );
        }
        // Force detailsLoaded:true regardless of whether the backend's
        // `detail` payload includes it — otherwise the card stays pinned
        // at stage 2 and the vehicle / lead / sales sections spin forever.
        return allContactsData.map((c, i) =>
          i === idx ? { ...c, ...detail, detailsLoaded: true } : c
        );
      };

      setCurrentCalls((prev) =>
        prev.map((call) => {
          if (call.callId !== callId) return call;
          const next = call.customerData
            ? { ...call.customerData, allContactsData: mergeIntoContacts(call.customerData.allContactsData) }
            : call.customerData;
          return { ...call, customerData: next, lastUpdate: new Date().toISOString() };
        })
      );

      if (currentCallIdRef.current === callId && showCustomerPopupRef.current) {
        setCustomerData((prev) =>
          prev ? { ...prev, allContactsData: mergeIntoContacts(prev.allContactsData) } : prev
        );
      }
      return;
    }

    if (message.type === 'complete_customer_data') {
      console.log('✅ COMPLETE CUSTOMER DATA RECEIVED:', message);
      const callId = message.callInfo?.callId;
      if (!callId) { console.warn('⚠️ No callId in complete_customer_data'); return; }
      if (message.isManualSearch) { console.log('✅ Manual search completed'); setIsSearching(false); }

      const hasVinData = message.vinSolutions?.contact && !message.vinSolutions.noVinSolutionsData;
      const hasTekionData = message.tekion && Object.keys(message.tekion).length > 0;
      const hasTwilioData = message.hasTwilioData && message.twilioData?.callerName;

      console.log('📊 Data availability:', { hasVinData, hasTekionData, hasTwilioData, callId });

      const buildCompleteData = () => ({
        ...(message.vinSolutions || {}), tekionData: message.tekion,
        noVinSolutionsData: !hasVinData, hasTekionData,
        twilioData: message.twilioData || null, hasTwilioData: !!hasTwilioData,
      });

      setCurrentCalls(prev =>
        prev.map(call => {
          if (call.callId !== callId) return call;
          console.log('🔄 Updating call with complete data:', callId);
          const newCustomerData = buildCompleteData();
          // Priority: Tekion → CRM (Vinsolutions) → Twilio
          const updatedCallerName = pickCallerName(newCustomerData, call.callerName);

          return {
            ...call, callerName: updatedCallerName,
            customerData: newCustomerData,
            tekionData: message.tekion, vinSolutionsLoaded: true, tekionLoaded: true,
            fullyLoaded: true, loadingStage: 5, lastUpdate: new Date().toISOString(),
          };
        })
      );

      if (currentCallIdRef.current === callId && showCustomerPopupRef.current) {
        const completeData = buildCompleteData();
        setCustomerData(completeData);
        setLoadingStage(5);
        // Priority: Tekion → CRM (Vinsolutions) → Twilio
        setCallInfo(prev => ({ ...prev, callerName: pickCallerName(completeData, prev?.callerName) }));
        console.log('✅ Popup updated with complete data (stage 5)');
      }
      return;
    }

    if (message.type === 'call_timer_started') {
      console.log('⏱️ Call timer started notification received');
      const { callId, startTime } = message;
      // Always patch the live call in the list (keyed by callId) — this keeps
      // the call cards accurate regardless of what the dashboard is showing.
      setCurrentCalls(prev => prev.map(c => c.callId === callId ? { ...c, timerStarted: true, startTime } : c));

      // Only repoint the global "current call" scalars when the dashboard
      // isn't pinned to a *different* call. Otherwise a new call connecting
      // would hijack an already-open (and possibly ended) call's status,
      // flipping the old call's badge to "Connected". Matches the
      // !showCustomerPopupRef guard used by progressive_update/call_notification.
      if (!showCustomerPopupRef.current || currentCallIdRef.current === callId) {
        setCallStartTime(startTime);
        currentCallIdRef.current = callId;
        setCurrentCallId(callId);
        setIsCallActive(true);
        setCallStatus('Connected');
      }
      return;
    }

    if (message.type === 'call_timer_ended') {
      console.log('⏱️ Call timer ended notification received');
      const { callId, duration } = message;
      setCurrentCalls(prev => prev.map(c => c.callId === callId ? { ...c, timerStarted: false, callDuration: duration, callEnded: true } : c));
      // Same guard as call_timer_started: don't clobber the open dashboard's
      // scalar status with another call's "ended" event.
      if (!showCustomerPopupRef.current || currentCallIdRef.current === callId) {
        setIsCallActive(false); setCallStartTime(null); setCallStatus('Ended'); setCallDuration(duration);
      }
      return;
    }

    if (message.type === 'call_ended') {
      console.log('📞 Call ended notification received');
      const { callId } = message;
      const endTime = Date.now();
      setIsCallActive(false);

      if (currentCallIdRef.current === callId) {
        if (callStatusRef.current === 'Ringing') setCallStatus('Missed');
        else if (callStatusRef.current === 'Connected') setCallStatus('Ended');
      }

      setCurrentCalls(prev =>
        prev.map(call => {
          if (call.callId !== callId) return call;
          const isUnanswered = !call.timerStarted;
          const ringDuration = isUnanswered && call.ringStartTime ? Math.floor((endTime - call.ringStartTime) / 1000) : 0;
          console.log(`📞 Call ${callId} ended - Unanswered: ${isUnanswered}, Ring Duration: ${ringDuration}s`);
          return { ...call, callEnded: true, status: 'Ended', isUnanswered, ringDuration, endTime };
        })
      );
      return;
    }

    if (message.type === 'multi_call_update') {
      const result = messageHandlers.handleMultiCallUpdate(message, setCurrentCalls);
      if (result && currentCallIdRef.current && showCustomerPopupRef.current) {
        if (currentCallIdRef.current === result.currentCallId) {
          console.log(`✅ Updating popup for current call ${result.currentCallId}`);
          if (result.stage >= 2) setCustomerData(prev => ({ ...(prev || {}), ...result.data }));
          // Priority: Tekion → CRM (Vinsolutions) → Twilio
          setCallInfo(prev => ({ ...prev, callerName: pickCallerName(result.data, prev?.callerName) }));
          setLoadingStage(result.stage);
        } else if (result.multiCall?.otherCallsData) {
          const match = result.multiCall.otherCallsData.find(o => o.callId === currentCallIdRef.current);
          if (match) {
            console.log(`✅ Updating popup for other call ${currentCallIdRef.current}`);
            if (match.stage >= 2) setCustomerData(prev => ({ ...(prev || {}), ...match.data }));
            // Priority: Tekion → CRM (Vinsolutions) → Twilio
            setCallInfo(prev => ({ ...prev, callerName: pickCallerName(match.data, prev?.callerName) }));
            setLoadingStage(match.stage);
          }
        }
      }
      return;
    }

    if (message.type === 'progressive_update') {
      const result = messageHandlers.handleProgressiveUpdate(message, setCurrentCalls, currentCallIdRef, audioRef);
      if (result.callId && !showCustomerPopupRef.current) setCurrentCallId(result.callId);

      if (currentCallIdRef.current === result.callId && showCustomerPopupRef.current) {
        setLoadingStage(prev => {
          if (result.stage <= prev) { console.log(`⏭️ Skipping popup stage ${result.stage} update - already at stage ${prev}`); return prev; }
          console.log(`✅ Updating popup for call ${result.callId} with stage ${result.stage} data`);
          return result.stage;
        });

        setCustomerData(prev => {
          if (prev?.tekionData && result.stage < 5) { console.log(`⏭️ Skipping data update - already have complete data`); return prev; }
          if (result.stage === 2) { const d = { ...(prev || {}), ...result.data }; console.log('✅ Stage 2 data merged:', d); return d; }
          if (result.stage === 3) { const d = { ...(prev || {}), ...result.data }; console.log('✅ Stage 3 data merged:', d); return d; }
          if (result.stage === 4) { console.log('✅ Stage 4 VinSolutions data set - waiting for Tekion'); return result.data; }
          return prev;
        });

        // Priority: Tekion → CRM (Vinsolutions) → Twilio
        setCallInfo(prev => ({ ...prev, callerName: pickCallerName(result.data, prev?.callerName) }));
      }
    } else if (message.type === 'search_error') {
      console.error('❌ Search error received:', message.error);
      if (message.isManualSearch) { setIsSearching(false); setLoadingStage(0); }
    } else if (message.type === 'call_notification') {
      const callId = messageHandlers.handleCallNotification(message, currentUser, setCurrentCalls, currentCallIdRef, audioRef);
      if (callId && !showCustomerPopupRef.current) setCurrentCallId(callId);
    } else if (message.type === 'authenticated') {
      console.log('✅ WebSocket authenticated for user:', currentUser.username);
      fetchCallHistory(currentUser.username, 1);
    } else if (message.type === 'connected') {
      console.log('✅ Server confirmed connection');
    } else if (message.type === 'payment_status_update') {
      // Webhook-driven push from MX → backend → here. The mounted
      // usePaymentStatusCheck hooks self-filter by (orderType, orderId)
      // and update their pill, so we just re-emit as a window event.
      try {
        window.dispatchEvent(new CustomEvent('payment_status_update', { detail: message }));
      } catch (err) {
        console.error('❌ Failed to dispatch payment_status_update event:', err);
      }
    }
  }, [currentUser]);

  const { sendMessage, connectWebSocket, MAX_RECONNECT_ATTEMPTS } = useWebSocket(
    currentUser, isAuthenticated, handleWebSocketMessage, setConnectionStatus, setReconnectAttempts,
    handleWebSocketAuthFailure
  );

  const fetchCallHistory = useCallback(async (usernameParam, page = 1) => {
    setLoadingHistory(true);
    setCallHistory([]);
    try {
      const data = await callHistoryService.fetchCallHistory(usernameParam, page);
      console.log('📄 Pagination response:', data);
      if (data.success) {
        setCallHistory(data.calls);
        setTotalCount(data.totalCount || 0);
        setTotalPages(Math.ceil((data.totalCount || 0) / callHistoryService.ITEMS_PER_PAGE));
        console.log(`✅ Loaded page ${page} with ${data.calls.length} calls`);
      }
    } catch (error) {
      console.error('Error fetching call history:', error);
      if (error.message === 'Authentication failed') {
        forceLogoutAndRedirect();
      } else {
        setCallHistory([]);
      }
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && currentUser) fetchCallHistory(currentUser.username, 1);
  }, [isAuthenticated, currentUser]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !loadingHistory && currentUser) {
      console.log(`📄 Changing to page ${newPage}`);
      setCurrentPage(newPage);
      fetchCallHistory(currentUser.username, newPage);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 Page became visible - checking connection...');
        if (isAuthenticated && currentUser) connectWebSocket();
      }
    };
    const handleOnline = () => { console.log('🌐 Network connection restored'); if (isAuthenticated && currentUser) connectWebSocket(); };
    const handleOffline = () => { console.log('📵 Network connection lost'); setConnectionStatus('offline'); };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    visibilityCheckIntervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible' && isAuthenticated && currentUser) {
        const now = Date.now();
        if (now - lastVisibilityCheckRef.current > 60000) {
          console.log('⏰ Long idle detected - verifying connection...');
          sendMessage({ type: 'ping' });
        }
        lastVisibilityCheckRef.current = now;
      }
    }, 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(visibilityCheckIntervalRef.current);
    };
  }, [isAuthenticated, currentUser, connectWebSocket, sendMessage]);

  const handleLoginSuccess = (user) => {
    const result = handleLogin(user);
    if (result.success) {
      setReconnectAttempts(0);
      setCurrentPage(1);
      fetchCallHistory(user.username, 1);
    }
  };

  const handleLogout = useCallback(async () => {
    await authLogout();
    setCurrentCalls([]); setCallHistory([]); setCurrentPage(1);
    setTotalPages(1); setTotalCount(0);
    showCustomerPopupRef.current = false;
    setShowCustomerPopup(false); setCustomerData(null);
    setConnectionStatus('disconnected'); setReconnectAttempts(0);
    setLoadingStage(0); setIsCallActive(false); setCallStartTime(null);
    currentCallIdRef.current = null; setCurrentCallId(null);
  }, [authLogout]);

  const closeCustomerPopup = useCallback(() => {
    setDashboardAnimationState('closing');
    setTimeout(() => {
      showCustomerPopupRef.current = false;
      setShowCustomerPopup(false); setCustomerData(null); setCallInfo(null);
      setLoadingStage(0);
      currentCallIdRef.current = null; setCurrentCallId(null);
      setDashboardAnimationState('closed');
    }, 300);
  }, []);

  const handleCallClick = (call) => {
    console.log('📞 Call clicked:', call.callId, 'loadingStage:', call.loadingStage);

    currentCallIdRef.current = call.callId;
    setCurrentCallId(call.callId);
    showCustomerPopupRef.current = true;
    setShowCustomerPopup(true);
    setDashboardAnimationState('opening');
    setTimeout(() => setDashboardAnimationState('open'), 300);

    // Priority: Tekion → CRM (Vinsolutions) → Twilio
    setCallInfo({
      callId: call.callId,
      callerNumber: call.callerNumber || call.phoneNumber,
      callerName: pickCallerName(call.customerData, call.callerName) || 'Loading...',
    });

    const hasExistingData = call.customerData && (
      call.customerData.contact?.contactId ||
      call.customerData.tekionData?.customerId ||
      call.customerData.hasTwilioData
    );
    const existingLoadingStage = call.loadingStage || 0;

    console.log('📊 Call data check:', {
      hasExistingData, existingLoadingStage,
      hasCustomerData: !!call.customerData,
      hasContact: !!call.customerData?.contact?.contactId,
      hasTekion: !!call.customerData?.tekionData?.customerId,
      hasTwilio: !!call.customerData?.hasTwilioData,
    });

    if (hasExistingData || existingLoadingStage >= 2) {
      console.log('✅ Using existing data for call:', call.callId);
      setCustomerData(call.customerData);
      setLoadingStage(existingLoadingStage >= 4 ? existingLoadingStage : 4);

      const isRinging = !call.timerStarted && !call.callEnded;
      const isConnected = call.timerStarted && !call.callEnded;
      if (isConnected) { setIsCallActive(true); setCallStartTime(call.startTime); setCallStatus('Connected'); }
      else if (isRinging) { setIsCallActive(true); setCallStartTime(null); setCallStatus('Ringing'); }
      else { setIsCallActive(false); setCallStartTime(null); setCallStatus(call.isUnanswered ? 'Missed' : 'Ended'); }
      setCallDuration(call.callDuration || call.ringDuration || 0);
    } else {
      console.log('🔄 Requesting fresh data for call:', call.callId);
      setCustomerData(null); setLoadingStage(1);
      setIsCallActive(false); setCallStartTime(null); setCallStatus(null); setCallDuration(0);

      const messageSuccess = sendMessage({
        type: 'fetch_historical_call', callId: call.callId,
        phoneNumber: call.callerNumber || call.phoneNumber,
        extension: call.extension || currentUser.username, timestamp: call.timestamp,
      });
      if (!messageSuccess) {
        console.error('❌ WebSocket not connected');
        if (call.customerData) { setCustomerData(call.customerData); setLoadingStage(call.loadingStage || 5); }
      }
    }
  };

  const handleReceptionistCallClick = (call) => {
    console.log('🖱️ Receptionist clicked call:', call.callId, 'at stage', call.loadingStage);

    currentCallIdRef.current = call.callId;
    setCurrentCallId(call.callId);
    showCustomerPopupRef.current = true;
    setShowCustomerPopup(true);
    setDashboardAnimationState('opening');
    setTimeout(() => setDashboardAnimationState('open'), 300);

    setCustomerData(null);
    setLoadingStage(1);
    setCallInfo({
      callId: call.callId,
      callerNumber: call.callerNumber || call.phoneNumber,
      callerName: 'Loading...',
    });
    setIsCallActive(false);
    setCallStartTime(null);

    const messageSuccess = sendMessage({
      type: 'fetch_historical_call', callId: call.callId,
      phoneNumber: call.callerNumber || call.phoneNumber,
      extension: call.extension || currentUser.username, timestamp: call.timestamp,
    });
    if (!messageSuccess) {
      console.error('❌ WebSocket not connected - falling back to cached data');
      setCustomerData(call.customerData || {});
      setLoadingStage(call.loadingStage || 5);
    }
  };

  const handleManualSearch = useCallback((phoneNumber) => {
    console.log('🔍 Manual search initiated for:', phoneNumber);
    setIsSearching(true);
    setSearchPhoneNumber(phoneNumber);

    const searchId = `search-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    currentCallIdRef.current = searchId;
    setCurrentCallId(searchId);
    showCustomerPopupRef.current = true;
    setShowCustomerPopup(true);
    setDashboardAnimationState('opening');
    setTimeout(() => setDashboardAnimationState('open'), 300);

    setCallInfo({
      callId: searchId, callerNumber: phoneNumber, callerName: 'Loading...',
      extension: currentUser.username, status: 'Search',
      timestamp: new Date().toISOString(), isManualSearch: true,
    });
    setCustomerData(null); setLoadingStage(1); setIsCallActive(false); setCallStartTime(null);

    const messageSuccess = sendMessage({
      type: 'manual_search', callId: searchId, phoneNumber,
      extension: currentUser.username, timestamp: new Date().toISOString(),
    });

    if (!messageSuccess) {
      console.error('❌ WebSocket not connected - falling back to REST API');
      const apiUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:7080/api/search-phone'
        : `/api/search-phone`;

      fetchWithAuth(apiUrl, {
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
      })
        .then(r => r.json())
        .then(data => {
          console.log('✅ REST API search response:', data);
          if (data.success && data.data) {
            setCustomerData({
              ...(data.data.vinSolutions || {}), tekionData: data.data.tekion,
              noVinSolutionsData: !data.hasVinData, hasTekionData: data.hasTekionData,
            });
            setLoadingStage(5);
          } else { console.error('❌ REST API search failed:', data.message); setLoadingStage(0); }
          setIsSearching(false);
        })
        .catch(err => {
          if (err.message === 'Authentication failed') return;
          console.error('❌ REST API search error:', err);
          setLoadingStage(0);
          setIsSearching(false);
        });
    }
  }, [currentUser, sendMessage]);

  // Lazy fetch for a peer VinSolutions contact whose details were skipped
  // on the initial call/search. Dashboard fires this when the user clicks
  // a contact pill whose `detailsLoaded` is false.
  const requestContactDetail = useCallback(({ callId, contactId, phoneNumber }) => {
    if (!callId || !contactId) return false;
    return sendMessage({
      type: 'fetch_contact_detail',
      callId,
      contactId,
      phoneNumber,
      extension: currentUser?.username,
      timestamp: new Date().toISOString(),
    });
  }, [currentUser, sendMessage]);

  const refreshCustomerData = useCallback(() => {
    if (!currentCallId || (!callInfo?.callerNumber && !searchPhoneNumber)) {
      console.warn('⚠️ Cannot refresh data: Missing callId or phone number', {
        currentCallId, callerNumber: callInfo?.callerNumber, searchPhoneNumber,
      });
      return;
    }

    const phoneNumber = callInfo?.callerNumber || searchPhoneNumber;
    console.log('🔄 Manually refreshing customer data for:', phoneNumber);
    setLoadingStage(1);

    if (callInfo?.isManualSearch || searchPhoneNumber) {
      sendMessage({
        type: 'manual_search', callId: currentCallId, phoneNumber,
        extension: currentUser?.username, timestamp: new Date().toISOString(),
      });
    } else {
      const currentCallObj = currentCalls.find(c => c.callId === currentCallId);
      sendMessage({
        type: 'fetch_historical_call', callId: currentCallId, phoneNumber,
        extension: currentUser?.username, timestamp: currentCallObj?.timestamp || new Date().toISOString(),
      });
    }
  }, [currentCallId, callInfo, searchPhoneNumber, currentUser, currentCalls, sendMessage]);

  const openSMS = useCallback((name, phoneNumber, draftMessage = '') => {
    console.log('📱 ============ SMS SEND TRIGGERED ============');
    console.log('📱 Phone number provided:', phoneNumber);
    console.log('📱 Calling number fallback:', callInfo?.callerNumber);
    console.log('📱 Customer name:', name);
    console.log('📱 ============================================');

    setSmsContact({
      id: Date.now(),
      name: name || 'Unknown Customer',
      phone: phoneNumber || callInfo?.callerNumber,
      avatar: '👤',
    });
    setSmsDraft(draftMessage || '');
    setIsSMSOpen(true);
  }, [callInfo]);

  const closeSMS = useCallback(() => {
    setIsSMSOpen(false);
    setSmsDraft('');
  }, []);

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'connected';
      case 'connecting': case 'reconnecting': return 'reconnecting';
      case 'offline': return 'offline';
      default: return 'disconnected';
    }
  };

  const getStatusText = () => {
    if (connectionStatus === 'reconnecting') return `reconnecting (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`;
    if (connectionStatus === 'offline') return 'offline - no internet';
    if (connectionStatus === 'timeout') return 'connection timeout';
    return connectionStatus;
  };

  const value = {
    isAuthenticated, currentUser, isReceptionist, isAuthLoading, handleLoginSuccess, handleLogout,
    connectionStatus, reconnectAttempts, connectWebSocket, sendMessage,
    MAX_RECONNECT_ATTEMPTS, getStatusColor, getStatusText,
    currentCalls, setCurrentCalls, callHistory, loadingHistory,
    fetchCallHistory, handleCallClick, handleReceptionistCallClick, handleManualSearch,
    reliableCalls, clearReliableCalls, activeCall,
    showCustomerPopup, customerData, callInfo, loadingStage,
    dashboardAnimationState, closeCustomerPopup, refreshCustomerData,
    requestContactDetail, currentCallId,
    isCallActive, callStartTime, callStatus, callDuration,
    isSearching, searchPhoneNumber,
    currentPage, totalPages, totalCount, handlePageChange,
    isSMSOpen, smsContact, smsDraft, openSMS, closeSMS,
    appConfig,
    audioRef,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}